import { useLocalThemeContext } from '../context';
import { HTMLMotionProps, LayoutGroup, motion } from 'framer-motion';
import { useId } from 'react';

interface SegmentedControlProps extends Pick<HTMLMotionProps<'div'>, 'className' | 'onLayoutAnimationComplete'> {
  items: Array<string>;
  setItem: (idx: number) => void;
  activeItemIndex?: number;
  activeItemLayoutId?: string;
  asTabs?: boolean;
  idPrefix?: string;
}

export const SegmentedControlAnimated = ({
  items,
  setItem,
  activeItemIndex,
  activeItemLayoutId = 'SegmentedControlActive',
  asTabs = false,
  idPrefix,
  className,
  onLayoutAnimationComplete,
}: SegmentedControlProps): JSX.Element => {
  const { colorPalette } = useLocalThemeContext();
  const generatedID = useId();
  const resolvedIDPrefix = idPrefix ?? `segmented-control-${generatedID}`;

  return (
    <div className={className}>
      <LayoutGroup>
        <ol
          aria-orientation={asTabs ? 'horizontal' : undefined}
          className="grid w-max grid-flow-col rounded-full bg-darkMode-gray-medium/10 p-1"
          role={asTabs ? 'tablist' : undefined}
        >
          {items.map((item, i) => {
            const isActive = i === activeItemIndex;
            return (
              <motion.li layout key={item} role={asTabs ? 'presentation' : undefined}>
                <button
                  aria-controls={asTabs ? `${resolvedIDPrefix}-panel-${i}` : undefined}
                  aria-selected={asTabs ? isActive : undefined}
                  id={asTabs ? `${resolvedIDPrefix}-tab-${i}` : undefined}
                  role={asTabs ? 'tab' : undefined}
                  tabIndex={asTabs ? (isActive ? 0 : -1) : undefined}
                  type="button"
                  onClick={() => setItem(i)}
                  onKeyDown={(event) => {
                    if (!asTabs) return;
                    const lastIndex = items.length - 1;
                    const nextIndex =
                      event.key === 'ArrowRight'
                        ? i === lastIndex
                          ? 0
                          : i + 1
                        : event.key === 'ArrowLeft'
                        ? i === 0
                          ? lastIndex
                          : i - 1
                        : event.key === 'Home'
                        ? 0
                        : event.key === 'End'
                        ? lastIndex
                        : null;
                    if (nextIndex === null) return;
                    event.preventDefault();
                    setItem(nextIndex);
                    const tabList = event.currentTarget.closest('[role="tablist"]');
                    (tabList?.querySelectorAll<HTMLElement>('[role="tab"]')[nextIndex] ?? null)?.focus();
                  }}
                  className="relative m-0 h-10 rounded-3xl px-5 py-3 text-[15px] font-bold leading-none text-palette-600"
                >
                  <motion.span
                    className="relative z-[1]"
                    transition={{ ease: 'easeInOut' }}
                    initial={{ color: colorPalette?.[600] }}
                    animate={isActive ? { color: colorPalette?.[1100] } : { color: colorPalette?.[600] }}
                  >
                    {item}
                  </motion.span>
                  {isActive && (
                    <motion.div
                      onLayoutAnimationComplete={onLayoutAnimationComplete}
                      transition={{ damping: 50, stiffness: 500, mass: 1 }}
                      layoutId={activeItemLayoutId}
                      className="absolute inset-0 flex items-center justify-center rounded-3xl bg-palette-600 text-[15px] font-bold text-darkMode-almostBlack"
                    />
                  )}
                </button>
              </motion.li>
            );
          })}
        </ol>
      </LayoutGroup>
    </div>
  );
};
